import aa from "search-insights";
import { v4 as uuidv4 } from "uuid";

import CookiePolicy, {
    cookiePolicyGroups,
} from "@utils/analytics/util/CookiePolicy";
import { StorageKeys } from "@utils/enums";
import { attemptMethod } from "@utils/general";
import { isDefined } from "@utils/helpers";
import { SafeLocalStorage } from "@utils/localStorage";

import * as AlgoliaParametersService from "@services/algolia/parameters/algoliaParameters.service";
import { getUserInfo } from "@services/tracking";
import TrackingService from "@services/tracking/tracking.service";

type AlgoliaRecord = {
    queryID: string;
    sku: string;
    timestamp: number;
    position: number;
};
type AlgoliaQueryRecord = Array<AlgoliaRecord>;

const initAlgoliaParameters = async () => {
    return (
        AlgoliaParametersService.getSessionsStorageAlgoliaParameters() ??
        (await AlgoliaParametersService.generateAlgoliaParameters(
            window.storeManager.dispatch,
        ))
    );
};

const setGAUserToken = () => {
    const userToken =
        TrackingService.getGoogleAnalyticsUserIdFromBrowserCookie("_ga");

    if (userToken) {
        aa("setUserToken", userToken);
    }
};

const setAnonymousUserToken = () => {
    const anonymousUserToken = `anonymous-${uuidv4()}`;
    aa("setUserToken", anonymousUserToken);
};

const registerOnConsentChangedHandler = () => {
    window.OneTrust?.OnConsentChanged(e => {
        const cookieList = e.detail;
        if (cookieList.includes(cookiePolicyGroups.personalisation)) {
            setGAUserToken();
        } else {
            setAnonymousUserToken();
        }
    });
};

class AlgoliaTrackingService {
    static getAlgoliaQueryTrackRecord() {
        return attemptMethod(
            () =>
                JSON.parse(
                    SafeLocalStorage.getItem(
                        StorageKeys.AlgoliaSearchQueryTracking,
                    ) || "",
                ),
            [],
        ) as AlgoliaQueryRecord;
    }

    static async init() {
        const { appId, searchApiKey } = await initAlgoliaParameters();

        aa("init", {
            partial: true,
            apiKey: searchApiKey,
            appId: appId,
        });

        aa("onAuthenticatedUserTokenChange", newToken => {
            if (newToken) {
                SafeLocalStorage.setItem(
                    StorageKeys.AlgoliaAuthenticatedUserToken,
                    newToken.toString(),
                );
            } else {
                SafeLocalStorage.removeItem(
                    StorageKeys.AlgoliaAuthenticatedUserToken,
                );
            }
        });

        aa("onUserTokenChange", newToken => {
            if (newToken) {
                SafeLocalStorage.setItem(
                    StorageKeys.AlgoliaUserToken,
                    newToken.toString(),
                );
            } else {
                SafeLocalStorage.removeItem(StorageKeys.AlgoliaUserToken);
            }
        });

        if (!CookiePolicy.isPersonalisationCookiesEnabled()) {
            setAnonymousUserToken();
        }

        if (CookiePolicy.isPersonalisationCookiesEnabled()) {
            setGAUserToken();
        }

        if (!isDefined(window.OneTrust)) {
            window.addEventListener(
                "onetrustloaded",
                registerOnConsentChangedHandler,
            );
        } else {
            registerOnConsentChangedHandler();
        }
    }

    static authenticate() {
        const clientID = getUserInfo().clientID;
        if (clientID) {
            aa("setAuthenticatedUserToken", clientID);
        }
    }

    static logout() {
        aa("setAuthenticatedUserToken", undefined);
    }

    static getUserToken() {
        return SafeLocalStorage.getItem(StorageKeys.AlgoliaUserToken)!;
    }

    static getAuthenticatedUserToken() {
        return (
            SafeLocalStorage.getItem(
                StorageKeys.AlgoliaAuthenticatedUserToken,
            ) || undefined
        );
    }

    static trackQuery(queryID: string, sku: string, position: number) {
        const algoliaQueryRecords =
            AlgoliaTrackingService.getAlgoliaQueryTrackRecord();
        algoliaQueryRecords.push({
            queryID,
            sku,
            timestamp: new Date().getTime(),
            position,
        });

        SafeLocalStorage.setItem(
            StorageKeys.AlgoliaSearchQueryTracking,
            JSON.stringify(algoliaQueryRecords),
        );
    }

    static getLastQueryTrackRecordFor(sku: string) {
        const algoliaQueryRecords =
            AlgoliaTrackingService.getAlgoliaQueryTrackRecord();
        return algoliaQueryRecords
            .sort((a, b) => b.timestamp - a.timestamp)
            .find(r => r.sku === sku);
    }

    static removeQueryTrackRecordFor(sku: string) {
        const algoliaQueryRecords =
            AlgoliaTrackingService.getAlgoliaQueryTrackRecord();
        const newRecords = algoliaQueryRecords.filter(r => r.sku !== sku);

        SafeLocalStorage.setItem(
            StorageKeys.AlgoliaSearchQueryTracking,
            JSON.stringify(newRecords),
        );
    }
}

export { AlgoliaTrackingService };
